<template>
    <div class="container-fluid pt-3">
        <div class="box col-12">
            <h1>{{ t('settings-products-title') }}</h1>

            <div style="margin-top: 15px; margin-bottom: 15px">
                <KvFilter
                    ref="filter-bar"
                    :defaultSelected="['search']"
                    :has-period-filter="false"
                    :has-branch-filter="false"
                    :filters="extraFilters"
                    :show-labels="true"
                    @changed="updateFilters"
                />
            </div>

            <modal ref="groupForm" :startsOpen="false" id="editGroup" @hide="cancelGroups" class="modal">
                <template slot="title">{{ t('settings-products-manage_groups') }} ({{ selectedProducts.length }} {{ t('settings-products-title').toLowerCase() }})</template>
                <template slot="body">
                    <form>
                        <div>
                            <label><input type="radio" v-model="groupAction" value="add" /> {{ t('settings-products-add_to_group') }}</label>
                            <label class="ml-2"><input type="radio" v-model="groupAction" value="remove" /> {{ t('settings-products-remove_from_group') }}</label>
                        </div>
                        <div>
                            <select v-model="group" class="form-control">
                                <option value="">{{ t('select') }}</option>
                                <option value="new" v-if="groupAction === 'add'">{{ t('settings-products-new_group') }}</option>
                                <option v-for="g in groups" :value="g.id" :key="g.id">{{ g.name }}</option>
                            </select>
                        </div>
                        <div class="mt-2">
                            <input
                                class="form-control"
                                v-if="group === 'new' && groupAction === 'add'"
                                type="text"
                                :placeholder="t('settings-products-group_name')"
                                v-model="newGroup"
                            />
                        </div>
                    </form>
                </template>
                <template slot="footer">
                    <button class="btn btn-success" @click="cancelGroups()">{{ t('cancel') }}</button>
                    <button class="btn btn-success" @click="confirmGroup()" :disabled="saving || !group">{{ t('confirm-confirm') }}</button>
                </template>
            </modal>

            <modal ref="editForm" :startsOpen="false" id="editForm" @hide="cancelEdit" class="modal">
                <template slot="title">{{ t('settings-products-batch_edit') }} ({{ selectedProducts.length }} {{ t('settings-products-title').toLowerCase() }})</template>
                <template slot="body">
                    <form>
                        <div class="field">
                            <label>{{ t('settings-products-repurchase_days') }}</label>
                            <input type="text" class="form-control" v-model="editFormRepurchasePeriod" required />
                        </div>
                    </form>
                </template>
                <template slot="footer">
                    <button class="btn btn-success" @click="cancelEdit()">{{ t('cancel') }}</button>
                    <button class="btn btn-success" @click="confirmEdit()" :disabled="saving">{{ t('save') }}</button>
                </template>
            </modal>

            <grid source="products/list" :quantityPerPage="50" :show-search="false" :filters="filters">
                <template v-slot:grid-actions>
                    <div>
                        <button class="btn btn-success" :disabled="selectedProducts.length === 0" @click="editGroups()">{{ t('settings-products-group') }}</button>
                        <button class="btn btn-success ml-2" :disabled="selectedProducts.length === 0" @click="edit()">{{ t('edit') }}</button>
                    </div>
                </template>
                <template v-slot:rows="grid">
                    <thead>
                        <tr>
                            <grid-th :grid="grid" style="width: 40px"></grid-th>
                            <grid-th :grid="grid" style="width: 60px">{{ t('image') }}</grid-th>
                            <grid-th :grid="grid">{{ t('name') }}</grid-th>
                            <grid-th :grid="grid">{{ t('category') }}</grid-th>
                            <grid-th :grid="grid">{{ t('price') }}</grid-th>
                            <grid-th :grid="grid">{{ t('settings-products-repurchase_days') }}</grid-th>
                            <grid-th :grid="grid">{{ t('status') }}</grid-th>
                            <grid-th :grid="grid">{{ t('settings-products-groups') }}</grid-th>
                            <grid-th :grid="grid" style="width: 50px"></grid-th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="product in grid.data" :key="product.id">
                            <td style="vertical-align: middle">
                                <input
                                    type="checkbox"
                                    :checked="selectedProducts.includes(product.id)"
                                    @change="toggleProduct($event, product.id)"
                                />
                            </td>
                            <td style="vertical-align: middle">
                                <img
                                    v-if="firstImage(product)"
                                    :src="firstImage(product)"
                                    class="product-thumb"
                                    alt=""
                                />
                                <span v-else class="product-thumb-placeholder fa fa-image"></span>
                            </td>
                            <td style="vertical-align: middle">
                                <router-link :to="'/settings/products/' + product.id" class="product-name-link" :title="product.name">
                                    {{ truncate(product.name, 50) }}
                                </router-link>
                            </td>
                            <td style="vertical-align: middle">
                                <span class="text-muted small" :title="product.category">{{ truncate(product.category, 20) || '—' }}</span>
                            </td>
                            <td style="vertical-align: middle; white-space: nowrap">
                                <template v-if="product.original_price && product.original_price != product.price">
                                    <span class="text-muted small" style="text-decoration: line-through">{{ currencySymbol }} {{ formatPrice(product.original_price) }}</span>
                                    <span class="ml-1 text-success font-weight-bold">{{ currencySymbol }} {{ formatPrice(product.price) }}</span>
                                </template>
                                <template v-else-if="product.price">
                                    <span>{{ currencySymbol }} {{ formatPrice(product.price) }}</span>
                                </template>
                                <span v-else class="text-muted">—</span>
                            </td>
                            <td style="vertical-align: middle">
                                <span v-if="saving === product.id">
                                    <span class="fa fa-spinner fa-spin"></span>
                                </span>
                                <input
                                    v-else
                                    type="text"
                                    class="form-control"
                                    :name="'product[' + product.id + ']'"
                                    :disabled="saving"
                                    :value="product.expected_reorder_period_days"
                                    @change="editSingleProduct(product.id, $event)"
                                />
                            </td>
                            <td style="vertical-align: middle">
                                <span class="badge" :class="statusBadgeClass(product.status)">{{ statusLabel(product.status) }}</span>
                            </td>
                            <td style="vertical-align: middle">
                                <span class="badge badge-primary mr-1" v-for="g in product.groups" :key="g.id">{{ g.groupName }}</span>
                            </td>
                            <td style="vertical-align: middle; text-align: center">
                                <router-link :to="'/settings/products/' + product.id" class="btn btn-sm btn-outline-secondary">
                                    <span class="fa fa-eye"></span>
                                </router-link>
                            </td>
                        </tr>
                    </tbody>
                </template>
            </grid>
        </div>
    </div>
</template>

<script>
import keviApi from "../keviApi";
import KvFilter from "../components/reports/kv-filter-bar.vue";

export default {
    components: {
        KvFilter,
    },
    data: () => ({
        loading: true,
        saving: false,
        selectedProducts: [],
        filters: {},
        editFormRepurchasePeriod: null,
        group: "",
        newGroup: null,
        groupAction: "add",
        groups: [],
    }),
    mounted() {
        this.loadGroups();
    },
    computed: {
        extraFilters() {
            return [
                {
                    component: "textfield",
                    label: this.t("search"),
                    name: "search",
                    removable: false,
                },
                // NOTE: for kv-filter-bar, "removable" filters must use a filter component
                // that supports emitting @remove / shows the remove UI. We keep the shared
                // components untouched and just pick the right filter type here.
                {
                    component: "kv-select",
                    label: this.t("status"),
                    name: "status",
                    removable: true,
                    options: {
                        options: [
                            { value: "active", label: this.t("active") },
                            { value: "out_of_stock", label: this.t("unavailable") },
                            { value: "paused", label: this.t("paused") },
                            { value: "archived", label: this.t("archived") },
                        ],
                    },
                },
                {
                    component: "textfield",
                    label: this.t("category"),
                    name: "category",
                    removable: true,
                },
            ];
        },
    },
    methods: {
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },
        updateFilters(values) {
            this.filters = values;
        },
        firstImage(product) {
            if (product.images && Array.isArray(product.images) && product.images.length > 0) {
                return product.images[0];
            }
            return null;
        },
        truncate(text, maxLength) {
            if (!text) return "";
            if (text.length <= maxLength) return text;
            return text.substring(0, maxLength) + "…";
        },
        formatPrice(value) {
            if (!value && value !== 0) return "";
            return parseFloat(value).toFixed(2).replace(".", ",");
        },
        statusLabel(status) {
            const map = {
                active: this.t('active'),
                out_of_stock: this.t('unavailable'),
                paused: this.t('paused'),
                archived: this.t('archived'),
            };
            return map[status] || status || "—";
        },
        statusBadgeClass(status) {
            const map = {
                active: "badge-success",
                out_of_stock: "badge-warning",
                paused: "badge-secondary",
                archived: "badge-dark",
            };
            return map[status] || "badge-light";
        },
        cancelEdit() {
            this.$refs.editForm.close();
        },
        cancelGroups() {
            this.$refs.groupForm.close();
        },
        edit() {
            this.$refs.editForm.open();
        },
        editGroups() {
            this.$refs.groupForm.open();
        },
        loadGroups() {
            let options = keviApi.getOptions("/products/groups", "GET");
            this.$http(options).then((response) => {
                this.groups = response.data.data;
            });
        },
        confirmEdit() {
            let options = keviApi.getOptions("/products/repurchase-period", "POST", {
                ids: this.selectedProducts,
                repurchasePeriod: this.editFormRepurchasePeriod,
            });
            this.saving = true;
            this.$http(options).then(
                (response) => {
                    this.saving = false;
                    this.editFormRepurchasePeriod = null;
                    this.$refs.editForm.close();
                    if (response.status === 200) {
                        this.$store.dispatch("notification/success", this.t('settings-products-products_updated'));
                    } else {
                        this.$store.dispatch("notification/warning", this.t('settings-products-something_went_wrong'));
                    }
                    this.filters = { ...this.filters };
                },
                () => {
                    this.saving = false;
                    this.$store.dispatch("notification/warning", this.t('settings-products-something_went_wrong'));
                },
            );
        },
        confirmGroup() {
            let options = keviApi.getOptions("/products/add-group", "POST", {
                ids: this.selectedProducts,
                groupAction: this.groupAction,
                newGroup: this.newGroup,
                group: this.group,
            });
            this.saving = true;
            this.$http(options).then(
                (response) => {
                    this.saving = false;
                    this.group = "";
                    this.newGroup = null;
                    this.groupAction = "add";
                    this.$refs.groupForm.close();
                    if (response.status === 200) {
                        this.$store.dispatch("notification/success", this.t('settings-products-group_updated'));
                    } else {
                        this.$store.dispatch("notification/warning", this.t('settings-products-something_went_wrong'));
                    }
                    this.filters = { ...this.filters };
                },
                () => {
                    this.saving = false;
                    this.$store.dispatch("notification/warning", this.t('settings-products-something_went_wrong'));
                },
            );
        },
        toggleProduct(event, id) {
            if (event.target.checked) {
                this.selectedProducts.push(id);
            } else {
                const index = this.selectedProducts.indexOf(id);
                this.selectedProducts.splice(index, 1);
            }
            this.selectedProducts = [...new Set(this.selectedProducts)];
        },
        toggleAll(event, all) {
            if (event.target.checked) {
                this.selectedProducts = all.map((p) => p.id);
            } else {
                this.selectedProducts = [];
            }
        },
        editSingleProduct(id, event) {
            let options = keviApi.getOptions("/products/repurchase-period", "POST", {
                ids: [id],
                repurchasePeriod: event.target.value,
            });
            this.saving = id;
            this.$http(options).then(
                (response) => {
                    this.saving = false;
                    if (response.status === 200) {
                        this.$store.dispatch("notification/success", this.t('settings-products-products_updated'));
                    } else {
                        this.$store.dispatch("notification/warning", this.t('settings-products-something_went_wrong'));
                    }
                    this.filters = { ...this.filters };
                },
                () => {
                    this.saving = false;
                    this.$store.dispatch("notification/warning", this.t('settings-products-something_went_wrong'));
                },
            );
        },
    },
};
</script>

<style scoped>
.product-thumb {
    width: 40px;
    height: 40px;
    object-fit: cover;
    border-radius: 6px;
    border: 1px solid #e5e7eb;
}
.product-thumb-placeholder {
    width: 40px;
    height: 40px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    background: #f3f4f6;
    color: #bbb;
    font-size: 18px;
}
.product-name-link {
    color: #333;
    font-weight: 500;
    text-decoration: none;
}
.product-name-link:hover {
    color: #2aaa4c;
    text-decoration: underline;
}
</style>
